import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import {
  fetchPhoneNumbers,
  purchasePhoneNumber,
  releasePhoneNumber,
  searchAvailablePhoneNumbers,
  updatePhoneNumber,
  clearAvailableNumbers,
  selectPhoneNumbers,
} from '../../store/slices/phoneNumbersSlice';
import {
  PurchasePhoneNumberPayload,
  SearchPhoneNumbersParams,
  UpdatePhoneNumberPayload,
} from '../../types/phone-numbers';
import { RootState } from '../../store';
import { logger } from '../../utils/logger';

export const usePhoneNumbers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const clientId = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation?.id ?? null
  );
  const { phoneNumbers, availableNumbers, isLoading, isSearching, isPurchasing, error } =
    useSelector(selectPhoneNumbers);

  const fetchAll = useCallback(async () => {
    if (!clientId) return;
    try {
      await dispatch(fetchPhoneNumbers(clientId)).unwrap();
    } catch (err) {
      logger.error('usePhoneNumbers: fetchAll failed', err);
      throw err;
    }
  }, [dispatch, clientId]);

  const searchAvailable = useCallback(
    async (params: SearchPhoneNumbersParams) => {
      if (!clientId) return;
      try {
        await dispatch(searchAvailablePhoneNumbers({ organisationId: clientId, params })).unwrap();
      } catch (err) {
        logger.error('usePhoneNumbers: searchAvailable failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const purchase = useCallback(
    async (payload: PurchasePhoneNumberPayload) => {
      if (!clientId) return;
      try {
        await dispatch(purchasePhoneNumber({ organisationId: clientId, payload })).unwrap();
      } catch (err) {
        logger.error('usePhoneNumbers: purchase failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const update = useCallback(
    async (phoneNumberId: number, payload: UpdatePhoneNumberPayload) => {
      if (!clientId) return;
      try {
        await dispatch(
          updatePhoneNumber({ organisationId: clientId, phoneNumberId, payload })
        ).unwrap();
      } catch (err) {
        logger.error('usePhoneNumbers: update failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const release = useCallback(
    async (phoneNumberId: number) => {
      if (!clientId) return;
      try {
        await dispatch(releasePhoneNumber({ organisationId: clientId, phoneNumberId })).unwrap();
      } catch (err) {
        logger.error('usePhoneNumbers: release failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const clearSearch = useCallback(() => {
    dispatch(clearAvailableNumbers());
  }, [dispatch]);

  return {
    phoneNumbers,
    availableNumbers,
    isLoading,
    isSearching,
    isPurchasing,
    error,
    fetchAll,
    searchAvailable,
    purchase,
    update,
    release,
    clearSearch,
  };
};
